/**
 * AuditDataGrid Component
 *
 * Feature: 123-audit-log-viewer
 * Description: Shared data grid wrapper for audit log pages
 * Constitution: v1.12.0
 */

import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Badge, Button, Spinner, DualScrollTable } from '../../../components/ui';
import { humanizeAuditLabel, actionBadgeVariant } from './auditLabels';

export interface ColumnDef<T> {
  id: string;
  label: string;
  width?: number | string;
  render: (row: T) => React.ReactNode;
}

interface AuditDataGridProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  getRowKey: (row: T) => string | number;
}

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Accepts ANY action string (backup/restore/plan/schedule/settings + future) — humanised + coloured
// by intent, so the audit grid never renders a raw snake_case value or an uncoloured badge.
export function ActionChip({ action }: { action: string }) {
  return (
    <Badge variant={actionBadgeVariant(action)} size="sm" isOutlined>
      {humanizeAuditLabel(action)}
    </Badge>
  );
}

export function ResultChip({ success }: { success: boolean }) {
  return (
    <Badge variant={success ? 'success' : 'error'} size="sm" isOutlined>
      {success ? 'Success' : 'Failed'}
    </Badge>
  );
}

export function ViewDetailsButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick}>
      View Details
    </Button>
  );
}

export default function AuditDataGrid<T>({
  columns,
  rows,
  total,
  page,
  pageSize,
  loading,
  emptyMessage = 'No audit entries found',
  onPageChange,
  onPageSizeChange,
  getRowKey,
}: AuditDataGridProps<T>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-[var(--color-text-secondary)]">{emptyMessage}</span>
      </div>
    );
  }

  // Server-side pagination state mirrors the previous MUI TablePagination.
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const navBtn =
    'inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 text-[var(--color-text-secondary)] enabled:hover:bg-[var(--color-bg-subtle)] enabled:hover:text-[var(--color-text-primary)] disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)]">
      <DualScrollTable>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              {columns.map((col) => (
                <th
                  key={col.id}
                  style={{ width: col.width }}
                  className="px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={getRowKey(row)}
                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-subtle)]"
              >
                {columns.map((col) => (
                  <td key={col.id} className="px-3 py-2 text-[var(--color-text-primary)]">
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </DualScrollTable>

      <div className="flex flex-wrap items-center justify-end gap-4 border-t border-[var(--color-border)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 text-sm text-[var(--color-text-primary)]"
          >
            {ROWS_PER_PAGE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <span>
          {from}–{to} of {total}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            aria-label="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(page + 1)}
            disabled={page >= lastPage}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(lastPage)}
            disabled={page >= lastPage}
            aria-label="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
